import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import SafetyNotice from '../../../components/base/SafetyNotice';
import AffiliateDisclosure from '../../../components/base/AffiliateDisclosure';
import { Link } from 'react-router-dom';
import { comfortProducts } from '../../../mocks/affiliate-products';
import SEOHead from '../../../components/base/SEOHead';

export default function ComfortNeedPage() {
  return (
    <div className="min-h-screen bg-sand">
      <SEOHead 
        title="Komfort & Liegen beim Seniorhund | Hund im Alter"
        description="Orthopädische Hundebetten, Rampen und Komfort-Lösungen für ältere Hunde. Gegen Druckstellen und für erholsamen Schlaf."
        keywords="orthopädisches Hundebett, Seniorhund Komfort, Hunderampe, Druckstellen Hund"
      />
      <Header />
      <main className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-teal">Start</Link>
            <span className="mx-2">/</span>
            <Link to="/beduerfnisse" className="hover:text-teal">Bedürfnisse</Link>
            <span className="mx-2">/</span>
            <span className="text-navy">Komfort & Liegen</span>
          </nav>

          {/* Page Header */}
          <div className="mb-12 bg-gradient-to-br from-teal/10 via-white to-sand rounded-3xl p-8 md:p-12 border border-teal/20">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-16 h-16 flex items-center justify-center bg-teal/20 rounded-2xl flex-shrink-0">
                <i className="ri-zzz-line text-5xl text-teal"></i>
              </div>
              <div className="flex-1">
                <h1 className="text-4xl md:text-5xl font-bold text-navy leading-tight mb-4">
                  Komfort & Liegen
                </h1>
                <p className="text-lg text-gray-600 leading-relaxed max-w-2xl">
                  Mit zunehmendem Alter verbringen Hunde mehr Zeit liegend. Eine komfortable, unterstützende Liegefläche ist entscheidend für ihr Wohlbefinden und ihre Gesundheit.
                </p>
                <div className="flex flex-wrap gap-3 mt-6">
                  <span className="px-4 py-2 bg-white rounded-full text-sm text-navy font-medium shadow-sm">
                    <i className="ri-moon-line mr-2 text-teal"></i>Erholsam
                  </span>
                  <span className="px-4 py-2 bg-white rounded-full text-sm text-navy font-medium shadow-sm">
                    <i className="ri-heart-3-line mr-2 text-teal"></i>Gelenkschonend
                  </span>
                  <span className="px-4 py-2 bg-white rounded-full text-sm text-navy font-medium shadow-sm">
                    <i className="ri-temp-hot-line mr-2 text-teal"></i>Wärmend
                  </span>
                </div>
              </div>
            </div>
          </div>

          <SafetyNotice />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Problem Recognition */}
              <section className="bg-white rounded-2xl p-10">
                <h2 className="text-3xl font-bold text-navy mb-6">
                  Woran Sie merken, dass dieses Bedürfnis besteht
                </h2>
                <ul className="space-y-4 text-lg text-gray-700 leading-relaxed">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal text-xl mt-1 flex-shrink-0"></i>
                    <span>Ihr Hund wechselt häufig die Liegeposition</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal text-xl mt-1 flex-shrink-0"></i>
                    <span>Kahle Stellen oder Druckstellen an Ellbogen und Hüfte</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal text-xl mt-1 flex-shrink-0"></i>
                    <span>Steifheit nach dem Aufstehen</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal text-xl mt-1 flex-shrink-0"></i>
                    <span>Ihr Hund meidet harte Liegeflächen</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal text-xl mt-1 flex-shrink-0"></i>
                    <span>Unruhiger Schlaf oder häufiges Aufwachen</span>
                  </li>
                </ul>
              </section>

              {/* Why This Happens */}
              <section className="bg-white rounded-2xl p-10">
                <h2 className="text-3xl font-bold text-navy mb-6">
                  Warum das im Alter passiert
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Mit zunehmendem Alter verlieren Hunde Muskelmasse und Fettpolster, die normalerweise als natürliche Polsterung dienen. Knochen liegen dadurch näher an der Hautoberfläche, was zu Druckstellen führen kann.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Gleichzeitig machen Arthrose und andere Gelenkprobleme das Liegen auf harten Oberflächen schmerzhaft. Die Durchblutung kann eingeschränkt sein, was die Regeneration während des Schlafs beeinträchtigt.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Ältere Hunde verbringen mehr Zeit liegend, was die Bedeutung einer komfortablen, unterstützenden Liegefläche noch wichtiger macht.
                </p>
              </section>

              {/* What Helps */}
              <section className="bg-white rounded-2xl p-10">
                <h2 className="text-3xl font-bold text-navy mb-6">
                  Was realistisch hilft
                </h2>
                <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                  <div>
                    <h3 className="text-xl font-semibold text-navy mb-3">Liegeflächen optimieren:</h3>
                    <ul className="space-y-3 ml-6">
                      <li className="flex items-start gap-3">
                        <span className="text-teal mt-1">•</span>
                        <span>Orthopädisches Hundebett mit Memory-Foam</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-teal mt-1">•</span>
                        <span>Mehrere Liegeplätze im Haus verteilen</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-teal mt-1">•</span>
                        <span>Weiche, waschbare Decken als zusätzliche Polsterung</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-navy mb-3">Alltag erleichtern:</h3>
                    <ul className="space-y-3 ml-6">
                      <li className="flex items-start gap-3">
                        <span className="text-teal mt-1">•</span>
                        <span>Erhöhte Futternäpfe für bequemeres Fressen</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-teal mt-1">•</span>
                        <span>Rampen statt Sprünge auf Sofa oder ins Auto</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-teal mt-1">•</span>
                        <span>Wärmekissen für schmerzende Gelenke</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-navy mb-3">Pflege und Vorbeugung:</h3>
                    <ul className="space-y-3 ml-6">
                      <li className="flex items-start gap-3">
                        <span className="text-teal mt-1">•</span>
                        <span>Regelmäßige Kontrolle auf Druckstellen</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-teal mt-1">•</span>
                        <span>Sanfte Massage zur Förderung der Durchblutung</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-teal mt-1">•</span>
                        <span>Gewichtskontrolle zur Entlastung der Gelenke</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Product Recommendations */}
              <section>
                <h2 className="text-3xl font-bold text-navy mb-6">
                  Hund-im-Alter empfiehlt
                </h2>
                
                <AffiliateDisclosure />

                <div className="space-y-8 mt-8">
                  {comfortProducts.map((product, index) => (
                    <div key={index} className="bg-white rounded-2xl p-8 shadow-sm">
                      <div className="flex flex-col md:flex-row gap-6">
                        <div className="w-full md:w-48 h-48 flex-shrink-0">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover rounded-xl"
                          />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-navy mb-4">
                            {product.name}
                          </h3>
                          <div className="mb-4">
                            <h4 className="text-base font-semibold text-teal mb-2">
                              Warum diese Lösung hilft:
                            </h4>
                            <p className="text-base text-gray-700 leading-relaxed">
                              {product.reason}
                            </p>
                          </div>
                          <div className="mb-6">
                            <h4 className="text-base font-semibold text-navy mb-2">
                              Geeignet für:
                            </h4>
                            <ul className="space-y-2">
                              {product.suitableFor.map((item, idx) => (
                                <li key={idx} className="flex items-center gap-2 text-base text-gray-700">
                                  <i className="ri-check-line text-teal"></i>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="flex flex-wrap gap-3">
                            <a
                              href={product.amazonLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-6 py-3 bg-navy text-white rounded-full font-medium hover:bg-navy-dark transition-colors whitespace-nowrap cursor-pointer"
                            >
                              Bei Amazon ansehen
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Customer Testimonials */}
              <section className="mt-12">
                <h2 className="text-3xl font-bold text-navy mb-6">
                  Das sagen andere Hundebesitzer
                </h2>
                <div className="space-y-6">
                  <div className="bg-white rounded-2xl p-8 shadow-sm">
                    <div className="flex items-start gap-4">
                      <img
                        src="https://public.readdy.ai/ai/img_res/9757374b74c9132e95cb54bdbe7763ea.jpg"
                        alt="Sabine M."
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-navy">Sabine M.</span>
                          <span className="text-sm text-gray-500">• Labrador, 13 Jahre</span>
                        </div>
                        <div className="flex items-center gap-1 mb-3">
                          <i className="ri-star-fill text-amber-400"></i>
                          <i className="ri-star-fill text-amber-400"></i>
                          <i className="ri-star-fill text-amber-400"></i>
                          <i className="ri-star-fill text-amber-400"></i>
                          <i className="ri-star-fill text-amber-400"></i>
                        </div>
                        <p className="text-gray-700 leading-relaxed">
                          "Unser alter Benny hatte ständig Druckstellen an den Ellbogen. Seit wir das orthopädische Bett haben, sind diese komplett verschwunden. Er schläft jetzt viel ruhiger durch und steht morgens deutlich entspannter auf. Die Investition hat sich absolut gelohnt."
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-8 shadow-sm">
                    <div className="flex items-start gap-4">
                      <img
                        src="https://readdy.ai/api/search-image?query=friendly%20senior%20man%20portrait%20natural%20lighting%20gentle%20smile%20casual%20clothing%20simple%20neutral%20background%20headshot%20professional%20quality&width=80&height=80&seq=testimonial-comfort-002&orientation=squarish"
                        alt="Klaus W."
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-navy">Klaus W.</span>
                          <span className="text-sm text-gray-500">• Golden Retriever, 11 Jahre</span>
                        </div>
                        <div className="flex items-center gap-1 mb-3">
                          <i className="ri-star-fill text-amber-400"></i>
                          <i className="ri-star-fill text-amber-400"></i>
                          <i className="ri-star-fill text-amber-400"></i>
                          <i className="ri-star-fill text-amber-400"></i>
                          <i className="ri-star-half-fill text-amber-400"></i>
                        </div>
                        <p className="text-gray-700 leading-relaxed">
                          "Die Rampe fürs Sofa war eine echte Erleichterung. Früher musste ich Max immer hochheben, was für uns beide anstrengend war. Jetzt geht er selbstständig rauf und runter. Einziger Nachteil: Er brauchte etwa eine Woche, um sich daran zu gewöhnen."
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-8 shadow-sm">
                    <div className="flex items-start gap-4">
                      <img
                        src="https://readdy.ai/api/search-image?query=friendly%20young%20woman%20portrait%20natural%20lighting%20warm%20expression%20casual%20clothing%20simple%20neutral%20background%20headshot%20professional%20quality&width=80&height=80&seq=testimonial-comfort-003&orientation=squarish"
                        alt="Anna K."
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-navy">Anna K.</span>
                          <span className="text-sm text-gray-500">• Dackel, 14 Jahre</span>
                        </div>
                        <div className="flex items-center gap-1 mb-3">
                          <i className="ri-star-fill text-amber-400"></i>
                          <i className="ri-star-fill text-amber-400"></i>
                          <i className="ri-star-fill text-amber-400"></i>
                          <i className="ri-star-fill text-amber-400"></i>
                          <i className="ri-star-fill text-amber-400"></i>
                        </div>
                        <p className="text-gray-700 leading-relaxed">
                          "Die erhöhten Futternäpfe haben bei unserer Lotte einen riesigen Unterschied gemacht. Sie frisst jetzt viel entspannter und würgt nicht mehr so oft. Auch das Trinken fällt ihr leichter. Hätte ich viel früher kaufen sollen!"
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-8 shadow-sm">
                    <div className="flex items-start gap-4">
                      <img
                        src="https://readdy.ai/api/search-image?query=mature%20friendly%20man%20portrait%20warm%20smile%20casual%20attire%20natural%20daylight%20approachable%20expression%20simple%20clean%20background%20professional%20headshot%20photograph&width=80&height=80&seq=testimonial-comfort-thomas-005&orientation=squarish"
                        alt="Thomas B."
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-navy">Thomas B.</span>
                          <span className="text-sm text-gray-500">• Schäferhund, 12 Jahre</span>
                        </div>
                        <div className="flex items-center gap-1 mb-3">
                          <i className="ri-star-fill text-amber-400"></i>
                          <i className="ri-star-fill text-amber-400"></i>
                          <i className="ri-star-fill text-amber-400"></i>
                          <i className="ri-star-fill text-amber-400"></i>
                          <i className="ri-star-fill text-amber-400"></i>
                        </div>
                        <p className="text-gray-700 leading-relaxed">
                          "Die Auto-Rampe ist stabil und rutschfest – genau das, was wir brauchten. Unser Rex hatte Angst vor dem Einsteigen, weil er einmal abgerutscht ist. Mit der Rampe geht er jetzt wieder gerne ins Auto. Qualität ist top."
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-32 space-y-6">
                {/* Info Box */}
                <div className="bg-sand border-l-4 border-teal rounded-xl p-6">
                  <h3 className="text-lg font-bold text-navy mb-4">
                    Wichtige Hinweise
                  </h3>
                  <ul className="space-y-3 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-information-line text-teal mt-0.5"></i>
                      <span>Wählen Sie waschbare Bezüge für einfache Hygiene</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-information-line text-teal mt-0.5"></i>
                      <span>Die richtige Größe ist entscheidend für Komfort</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-information-line text-teal mt-0.5"></i>
                      <span>Geben Sie Ihrem Hund Zeit, neue Liegeplätze zu akzeptieren</span>
                    </li>
                  </ul>
                </div>

                {/* Vet Notice */}
                <div className="bg-yellow-50 border-l-4 border-orange-400 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-navy mb-3 flex items-center gap-2">
                    <i className="ri-stethoscope-line text-orange-500"></i>
                    Wann zum Tierarzt?
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-orange-500 mt-0.5">•</span>
                      <span>Offene oder entzündete Druckstellen</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-500 mt-0.5">•</span>
                      <span>Starke Schmerzen beim Liegen oder Aufstehen</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-500 mt-0.5">•</span>
                      <span>Plötzliche Verschlechterung der Mobilität</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-500 mt-0.5">•</span>
                      <span>Appetitlosigkeit oder Verhaltensänderungen</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-500 mt-0.5">•</span>
                      <span>Anhaltende Unruhe trotz komfortabler Liegefläche</span>
                    </li>
                  </ul>
                </div>

                {/* Related Needs */}
                <div className="bg-white rounded-xl p-6">
                  <h3 className="text-lg font-bold text-navy mb-4">
                    Weitere Bedürfnisse
                  </h3>
                  <ul className="space-y-3">
                    <li>
                      <Link to="/beduerfnisse/mobilitaet-aufstehen" className="text-base text-teal hover:underline cursor-pointer">
                        Mobilität & Aufstehen →
                      </Link>
                    </li>
                    <li>
                      <Link to="/beduerfnisse/hygiene-inkontinenz" className="text-base text-teal hover:underline cursor-pointer">
                        Hygiene & Inkontinenz →
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
